/**
 * useKeyboardShortcuts — registra atalhos globais com suporte a sequencias
 * (chord) tipo "g u" (vai para usuarios).
 *
 * Sprint 6 do plano. Inspirado em GitHub/Linear.
 *
 * Uso:
 *   useKeyboardShortcuts({
 *     "g u": () => navigate("/acessos/usuarios"),
 *     "g c": () => navigate("/acessos/cargos"),
 *     "n": () => navigate("/acessos/usuarios/novo"),
 *     "/": () => focusSearch(),
 *     "?": () => setHelpOpen(true),
 *   });
 *
 * Regras:
 * - Ignora atalhos quando o foco esta em input/textarea/contenteditable
 *   (exceto "?" que abre o overlay de ajuda sempre).
 * - Sequencias com espaco: a tecla precisa ser pressionada dentro de 1.5s
 *   apos a anterior. Senao reseta.
 * - Permite Esc para limpar a sequencia em andamento.
 */
import { useEffect, useRef } from "react";

const SEQ_TIMEOUT_MS = 1500;

function _ehInput(target) {
  if (!target) return false;
  const tag = (target.tagName || "").toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") return true;
  if (target.isContentEditable) return true;
  return false;
}

function _keyToToken(event) {
  // Normaliza: "?" => "?", "g" => "g", "Escape" => "Escape", etc.
  if (event.key === " ") return "Space";
  return event.key.length === 1 ? event.key.toLowerCase() : event.key;
}

export function useKeyboardShortcuts(shortcuts) {
  const buffer = useRef("");
  const lastKeyTs = useRef(0);
  const shortcutsRef = useRef(shortcuts);

  // Mantemos a ref atualizada sem re-bind do listener.
  useEffect(() => {
    shortcutsRef.current = shortcuts;
  }, [shortcuts]);

  useEffect(() => {
    function onKeyDown(event) {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      const target = event.target;
      const ehInputFocado = _ehInput(target);

      // Sempre permite "?" para abrir help (escapando se o foco esta em input).
      if (event.key === "?" && !ehInputFocado) {
        const fn = shortcutsRef.current["?"];
        if (fn) {
          event.preventDefault();
          fn(event);
          return;
        }
      }

      // "/" focar busca — comportamento de GitHub.
      if (event.key === "/" && !ehInputFocado) {
        const fn = shortcutsRef.current["/"];
        if (fn) {
          event.preventDefault();
          fn(event);
          return;
        }
      }

      if (event.key === "Escape") {
        buffer.current = "";
        const fn = shortcutsRef.current["Escape"];
        if (fn) fn(event);
        return;
      }

      if (ehInputFocado) return;

      const token = _keyToToken(event);
      const agora = Date.now();
      // Se passou muito tempo desde a ultima tecla, reseta o buffer.
      if (agora - lastKeyTs.current > SEQ_TIMEOUT_MS) {
        buffer.current = "";
      }
      lastKeyTs.current = agora;

      const novoBuffer = buffer.current ? `${buffer.current} ${token}` : token;

      const fn = shortcutsRef.current[novoBuffer];
      if (fn) {
        event.preventDefault();
        fn(event);
        buffer.current = "";
        return;
      }

      // Verifica se o buffer e prefixo de algum atalho conhecido.
      // Se for, mantemos. Se nao, dropamos e tentamos somente com a tecla nova.
      const tempBuffer = novoBuffer;
      const algumPrefixoMatch = Object.keys(shortcutsRef.current).some(
        (chave) =>
          chave.startsWith(tempBuffer + " ") || chave === tempBuffer
      );
      if (algumPrefixoMatch) {
        buffer.current = tempBuffer;
      } else {
        // Tenta atalho de uma tecla so.
        const fnSingle = shortcutsRef.current[token];
        if (fnSingle) {
          event.preventDefault();
          fnSingle(event);
        }
        buffer.current = "";
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);
}

export default useKeyboardShortcuts;
